// src/components/Books.jsx
import React, { useContext, useEffect } from 'react';
import { BookEstimate } from '../../Context/BookEstimate';
import { Input, Selectbox } from '../../Shared';
import { allboxes } from './Calcs';

const Books = ({ closeBook, setEstitems, estitems, editingItem = null }) => {
    const {
        items,
        setItems,
        checkquans,
        quanbox,
        bookinfo,
        setBookinfo,
        coilinfo,
        setCoilinfo,
        binding,
        setBinding,
        getlabor,
        getmat,
        changecoilinfo,
        reset,
        setReset,
    } = useContext(BookEstimate);

    // Load editingItem
    useEffect(() => {
        if (editingItem) {
            setBookinfo({
                ...bookinfo,
                width: editingItem.width || 8.5,
                length: editingItem.length || 11,
                spine: editingItem.spine || 0,
                textpages: editingItem.textpages || 0,
                textthickness: editingItem.textthickness || '',
                coverpages: editingItem.coverpages || 0,
                coverthickness: editingItem.coverthickness || '',
                otherpages: editingItem.otherpages || 0,
                otherthickness: editingItem.otherthickness || '',
                tabs: editingItem.tabs || 0,
                slipsheet: editingItem.slipsheet || false,
                collatepu: editingItem.collatepu || '',
                insert: editingItem.insert || '',
                cuts: editingItem.cuts || '',
                punchmr: editingItem.punchmr || 50,
                coilmr: editingItem.coilmr || 50,
            });
            setBinding({ type: editingItem.binding || '' });
        }
    }, [editingItem]);

    // Helpers
    const updateItemField = (itemId, section, field, value) => {
        const newItems = items.map(item =>
            item.id === itemId
                ? { ...item, [section]: { ...item[section], [field]: value } }
                : item
        );
        setItems(newItems);
        setReset(!reset);
    };

    const updatePunchType = (itemId, part, value) => {
        const speed = value === 'Auto' ? 7500 : value === 'Hand' ? 3500 : 0;
        const newItems = items.map(item => {
            if (item.id !== itemId) return item;
            return {
                ...item,
                punching: {
                    ...item.punching,
                    [part]: { ...item.punching[part], punchtype: value, punchspeed: speed },
                },
            };
        });
        setItems(newItems);
        setReset(!reset);
    };

    const handleBoxChange = (itemId, e) => {
        const boxId = Number(e.target.value);
        const box = allboxes.find(b => b.boxid === boxId);
        if (!box) return;

        const item = items.find(i => i.id === itemId);
        const needed = Math.ceil(
            (Number(bookinfo?.length || 0) * Number(bookinfo?.width || 0) *
                Number(coilinfo?.decimal || 0) * Number(item?.quantity || 0)) /
            Number(box.boxholds || 1)
        );
        const cost = needed * Number(box.boxcost || 0);

        const newItems = items.map(i =>
            i.id === itemId
                ? { ...i, boxes: { ...i.boxes, boxid: box.boxid, needed, cost } }
                : i
        );
        setItems(newItems);
        setReset(!reset);
    };
 
    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/70">
            <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-7xl mx-4 my-8 max-h-[90vh] overflow-y-auto p-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                    {editingItem ? 'Edit Book' : 'Add Book'}
                </h2>

                {/* Global Inputs */}
                <div className="space-y-10">
                    {/* Dimensions */}
                    <div className="bg-gray-50 p-6 rounded-xl">
                        <h3 className="font-bold text-lg mb-4">Dimensions</h3>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm mb-1">Width (in)</label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    value={bookinfo.width || ''}
                                    onChange={e => setBookinfo({ ...bookinfo, width: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm mb-1">Length (in)</label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    value={bookinfo.length || ''}
                                    onChange={e => setBookinfo({ ...bookinfo, length: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm mb-1">Spine (in)</label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={bookinfo.spine || ''}
                                    onChange={e => setBookinfo({ ...bookinfo, spine: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Pages */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-gray-50 p-6 rounded-xl">
                            <h3 className="font-bold mb-4">Text Pages</h3>
                            <Input
                                type="number"
                                value={bookinfo.textpages || ''}
                                onChange={e => setBookinfo({ ...bookinfo, textpages: e.target.value })}
                            />
                        </div>
                        <div className="bg-gray-50 p-6 rounded-xl">
                            <h3 className="font-bold mb-4">Cover Pages</h3>
                            <Input
                                type="number"
                                value={bookinfo.coverpages || ''}
                                onChange={e => setBookinfo({ ...bookinfo, coverpages: e.target.value })}
                            />
                        </div>
                        <div className="bg-gray-50 p-6 rounded-xl">
                            <h3 className="font-bold mb-4">Other/Inserts</h3>
                            <Input
                                type="number"
                                value={bookinfo.otherpages || ''}
                                onChange={e => setBookinfo({ ...bookinfo, otherpages: e.target.value })}
                            />
                        </div>
                    </div>

                    {/* Binding */}
                    <div className="bg-gray-50 p-6 rounded-xl">
                        <h3 className="font-bold mb-4">Binding Type</h3>
                        <Selectbox
                            value={binding.type || ''}
                            onChange={e => setBinding({ type: e.target.value })}
                        >
                            <option value="">None</option>
                            <option value="Wire-O">Wire-O</option>
                            <option value="Plastikol">Plastikol</option>
                        </Selectbox>
                    </div>

                    {/* Misc Options */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm mb-1">Tabs</label>
                            <Input
                                type="number"
                                value={bookinfo.tabs || ''}
                                onChange={e => setBookinfo({ ...bookinfo, tabs: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Collate P/U</label>
                            <Input
                                value={bookinfo.collatepu || ''}
                                onChange={e => setBookinfo({ ...bookinfo, collatepu: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Insert</label>
                            <Input
                                value={bookinfo.insert || ''}
                                onChange={e => setBookinfo({ ...bookinfo, insert: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Cuts</label>
                            <Input
                                value={bookinfo.cuts || ''}
                                onChange={e => setBookinfo({ ...bookinfo, cuts: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Punch M/R $</label>
                            <Input
                                type="number"
                                step="0.01"
                                value={bookinfo.punchmr || 50}
                                onChange={e => setBookinfo({ ...bookinfo, punchmr: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Coil M/R $</label>
                            <Input
                                type="number"
                                step="0.01"
                                value={bookinfo.coilmr || 50}
                                onChange={e => setBookinfo({ ...bookinfo, coilmr: e.target.value })}
                            />
                        </div>
                    </div>
                </div>

                {/* Quantity Input */}
                <div className="mb-10">
                    <label className="font-bold block mb-2">Add Quantity (press Enter)</label>
                    <Input
                        ref={quanbox}
                        type="number"
                        placeholder="100, 500, 1000..."
                        onKeyDown={checkquans}
                        className="w-64 p-2 border rounded"
                    />
                </div>

                {/* PocketFolder-Style Cards */}
                {items.length > 0 && (
                    <div className="mt-10">
                        <h2 className="text-2xl font-bold text-gray-800 mb-5">Quantities & Pricing</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                            {items.map((item) => {
                                const labor = Number(getlabor(item) || 0);
                                const material = Number(getmat(item) || 0);
                                const total = labor + material;
                                const perUnit = item.quantity > 0 ? total / item.quantity : 0;

                                return (
                                    <div
                                        key={item.id}
                                        className="border rounded-xl p-5 bg-white shadow-lg relative"
                                        style={{ padding: '20px' }}
                                    >
                                        <button
                                            onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))}
                                            className="absolute top-2 right-2 bg-red-500 text-white w-7 h-7 rounded-full text-sm flex items-center justify-center hover:bg-red-600"
                                        >
                                            X
                                        </button>

                                        <div className="text-2xl font-bold text-blue-700 mb-1">
                                            {Number(item.quantity).toLocaleString()}
                                        </div>

                                        <div className="text-sm text-gray-600 mb-4">
                                            {binding.type || 'No binding'} • {bookinfo.totalpages || 0} pages
                                        </div>

                                        <div className="mt-4 space-y-1 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Labor:</span>
                                                <strong>${labor.toFixed(2)}</strong>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Material:</span>
                                                <strong>${material.toFixed(2)}</strong>
                                            </div>

                                            {/* Coiling Info */}
                                            {item.coiling?.coiltotal > 0 && (
                                                <>
                                                    <div className="flex justify-between text-amber-600 mt-2">
                                                        <span>Coil Material ({item.coiling.numboxes > 0 ? 'Boxes' : 'Spools'}):</span>
                                                        <strong>${Number(item.coiling.coiltotal).toFixed(2)}</strong>
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Loops: {item.coiling.coilloops} • Used: {item.coiling.usage}
                                                    </div>
                                                </>
                                            )}

                                            {/* Other colored lines */}
                                            {item.punching?.text?.punchtotal > 0 && (
                                                <div className="flex justify-between text-purple-600">
                                                    <span>Text Punch:</span>
                                                    <strong>${Number(item.punching.text.punchtotal).toFixed(2)}</strong>
                                                </div>
                                            )}
                                            {item.boxes?.cost > 0 && (
                                                <div className="flex justify-between text-indigo-600">
                                                    <span>Boxes:</span>
                                                    <strong>${Number(item.boxes.cost).toFixed(2)}</strong>
                                                </div>
                                            )}
                                        </div>

                                        <div className="border-t-2 border-gray-300 my-3"></div>

                                        <div className="text-xl font-bold text-green-600">
                                            Total: ${total.toFixed(2)}
                                        </div>

                                        <div className="text-lg font-bold text-green-600 mt-1">
                                            {Number(item.quantity).toLocaleString()} pcs
                                        </div>
                                        <div className="text-sm text-gray-600 mt-1">
                                            ${perUnit.toFixed(4)} / unit
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Save / Cancel */}
                <div className="mt-12 flex justify-end gap-6">
                    <button
                        onClick={() => {
                            // Your save logic here
                            closeBook();
                        }}
                        className="px-10 py-4 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
                    >
                        {editingItem ? 'Update Book' : 'Add to Estimate'}
                    </button>
                    <button
                        onClick={closeBook}
                        className="px-10 py-4 bg-gray-300 rounded-lg hover:bg-gray-400"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Books;